/**
 * AmazonCatalogLookup Component
 *
 * Full product lookup: catalog + pricing + A+ content.
 * Uses /amazon/catalog/full for combined data.
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { lookupFullProduct, searchCatalog } from '@/lib/api/amazon-sp-api'
import type { FullProductItem, CatalogItem } from '@/lib/api/amazon-sp-api'
import { Card, CardContent } from '@/components/v2/ui/Card'
import { CatalogSearchForm } from './CatalogSearchForm'
import { FullProductDetail } from './FullProductDetail'

export function AmazonCatalogLookup() {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [searchType, setSearchType] = useState('asin')
  const [marketplace, setMarketplace] = useState('de')
  const [fullResult, setFullResult] = useState<FullProductItem | null>(null)
  const [results, setResults] = useState<CatalogItem[]>([])
  const [showRaw, setShowRaw] = useState(false)

  const lookupMutation = useMutation({
    mutationFn: () => {
      const q = query.trim()
      if (searchType === 'asin') return lookupFullProduct(q.toUpperCase(), marketplace)
      if (searchType === 'ean') return searchCatalog({ identifiers: [q], identifier_type: 'EAN', marketplace })
      return searchCatalog({ keywords: [q], marketplace })
    },
    onSuccess: (data) => {
      if ('item' in data && data.item) {
        if ('pricing' in data.item) {
          setFullResult(data.item as FullProductItem)
          setResults([])
        } else {
          setFullResult(null)
          setResults([])
        }
      } else if ('items' in data && data.items) {
        setResults(data.items as CatalogItem[])
        setFullResult(null)
      } else {
        toast.error(data.error || t('amazonSpApi.errors.notFound'))
      }
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return
    lookupMutation.mutate()
  }

  return (
    <div className="space-y-4">
      <CatalogSearchForm
        query={query}
        searchType={searchType}
        marketplace={marketplace}
        isPending={lookupMutation.isPending}
        onQueryChange={setQuery}
        onSearchTypeChange={setSearchType}
        onMarketplaceChange={setMarketplace}
        onSubmit={handleSubmit}
      />

      {results.length > 1 && (
        <CatalogResultsList
          results={results}
          resultsFoundLabel={t('amazonSpApi.lookup.resultsFound')}
          onSelect={(_item) => { setResults([]); setFullResult(null) }}
        />
      )}

      {fullResult && (
        <FullProductDetail
          item={fullResult}
          showRaw={showRaw}
          onToggleRaw={() => setShowRaw(!showRaw)}
        />
      )}
    </div>
  )
}

function CatalogResultsList({
  results,
  resultsFoundLabel,
  onSelect,
}: {
  results: CatalogItem[]
  resultsFoundLabel: string
  onSelect: (item: CatalogItem) => void
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm text-neutral-500">{results.length} {resultsFoundLabel}</p>
      {results.map((item) => (
        <Card key={item.asin} className="cursor-pointer hover:border-amber-400 transition-colors" onClick={() => onSelect(item)}>
          <CardContent className="p-3 flex items-center gap-3">
            {item.images[0] && (
              <img src={item.images[0].url} alt="" className="h-12 w-12 object-contain rounded" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{item.title || item.asin}</p>
              <p className="text-xs text-neutral-400">{item.asin} · {item.brand || '—'}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
